/**
 * Rescoring Store
 *
 * Persists and retrieves RescoringEvent records. Each event documents a score
 * revision triggered by new information (technological breakthrough, market
 * shift, etc.) with full provenance: what dimension changed, by how much,
 * and whether a prioritization revisitation is warranted.
 *
 * Persistence: Supabase (rescoring_events table) — no localStorage cache.
 */

import { create } from 'zustand'
import {
  generateUUID,
  getSafeSupabaseClient,
  getCurrentWorkspaceId,
} from '../../lib/supabaseSync'
import type { RescoringEvent, TriggerType, RescoringAttributeKey } from '../../types/prioritization/rescoringEvents'

const TABLE = 'rescoring_events'

// ─── Store shape ──────────────────────────────────────────────────────────────

export interface NewRescoringEventParams {
  itemId: string
  triggerType: TriggerType
  triggerDescription: string
  attributeKey: RescoringAttributeKey
  previousScore: number
  revisedScore: number
  rationale: string
  revisitationRequired: boolean
  revisitationNotes?: string
  revisitationDue?: string
  createdBy?: string
}

interface RescoringState {
  /** Events keyed by itemId */
  eventsByItem: Record<string, RescoringEvent[]>
  loadingItemIds: Set<string>
  error: string | null

  fetchForItem: (itemId: string) => Promise<void>
  addEvent: (params: NewRescoringEventParams) => Promise<RescoringEvent | null>
  getEventsForItem: (itemId: string) => RescoringEvent[]
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function toAppFormat(row: Record<string, unknown>): RescoringEvent {
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    itemId: row.item_id as string,
    triggerType: row.trigger_type as TriggerType,
    triggerDescription: row.trigger_description as string,
    attributeKey: row.attribute_key as RescoringAttributeKey,
    previousScore: Number(row.previous_score),
    revisedScore: Number(row.revised_score),
    rationale: row.rationale as string,
    revisitationRequired: Boolean(row.revisitation_required),
    revisitationNotes: (row.revisitation_notes as string) ?? undefined,
    revisitationDue: (row.revisitation_due as string) ?? undefined,
    createdBy: (row.created_by as string) ?? undefined,
    createdAt: row.created_at as string,
  }
}

// ─── Store ────────────────────────────────────────────────────────────────────

export const useRescoringStore = create<RescoringState>((set, get) => ({
  eventsByItem: {},
  loadingItemIds: new Set(),
  error: null,

  getEventsForItem: (itemId: string) => get().eventsByItem[itemId] ?? [],

  fetchForItem: async (itemId: string) => {
    const supabase = getSafeSupabaseClient()
    if (!supabase) return

    set((s) => ({ loadingItemIds: new Set([...s.loadingItemIds, itemId]) }))

    try {
      const { data, error } = await supabase
        .from(TABLE)
        .select('*')
        .eq('item_id', itemId)
        .order('created_at', { ascending: false })

      if (error) throw error

      const events = (data ?? []).map(toAppFormat)
      set((s) => ({
        eventsByItem: { ...s.eventsByItem, [itemId]: events },
        error: null,
      }))
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch rescoring events'
      set({ error: msg })
      console.error('[RescoringStore] fetchForItem error:', err)
    } finally {
      set((s) => {
        const next = new Set(s.loadingItemIds)
        next.delete(itemId)
        return { loadingItemIds: next }
      })
    }
  },

  addEvent: async (params: NewRescoringEventParams) => {
    const supabase = getSafeSupabaseClient()
    const workspaceId = getCurrentWorkspaceId()
    if (!supabase || !workspaceId) {
      set({ error: 'Cannot save: Supabase or workspace not configured' })
      return null
    }

    const id = generateUUID()
    const now = new Date().toISOString()

    const row = {
      id,
      workspace_id: workspaceId,
      item_id: params.itemId,
      trigger_type: params.triggerType,
      trigger_description: params.triggerDescription,
      attribute_key: params.attributeKey,
      previous_score: params.previousScore,
      revised_score: params.revisedScore,
      rationale: params.rationale,
      revisitation_required: params.revisitationRequired,
      revisitation_notes: params.revisitationNotes ?? null,
      revisitation_due: params.revisitationDue ?? null,
      created_by: params.createdBy ?? null,
      created_at: now,
    }

    try {
      const { error } = await supabase.from(TABLE).insert(row)
      if (error) throw error

      const event = toAppFormat(row as Record<string, unknown>)
      set((s) => ({
        eventsByItem: {
          ...s.eventsByItem,
          [params.itemId]: [event, ...(s.eventsByItem[params.itemId] ?? [])],
        },
        error: null,
      }))
      return event
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save rescoring event'
      set({ error: msg })
      console.error('[RescoringStore] addEvent error:', err)
      return null
    }
  },
}))
